import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createTenantStorage } from './tenantStorage';
import { toPersistedChatTabMentions } from './chatTabMentionPersistence';
import { useMemo, useRef } from 'react';
import type { MentionEntity } from '@terra/shared-dto-chat-types';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { useTargetId, useTargetType, useProjectStore } from './projectStore';
import { TARGET_TYPE } from '../utils/target-scope.utils';
import { useChatInstancesStore } from './chatInstancesStore';
import { useUnreadTabsStore } from './unreadTabsStore';
import { isEqual } from 'radash';

export type ChatTabType = {
  tabId: string;
  chatId: string;
  conversationId: string | null;
  title: string;
  hasCustomTitle?: boolean;
  targetId?: string;
  targetType?: TargetTypeType;
  mentions?: Array<{
    id: string;
    label: string;
    type?: 'parent' | 'child' | 'action' | 'resource';
    mentionEntity?: MentionEntity;
  }>;
  draftInput?: string;
  skillDraftId?: string;
  linkedObject?: {
    kind: string;
    id: string;
    label?: string;
  };
};

export function findChatTabByThreadId({
  chatTabs,
  threadId,
}: {
  chatTabs: ChatTabType[];
  threadId: string;
}): ChatTabType | undefined {
  return chatTabs.find(
    (tab) => tab.tabId === threadId || tab.chatId === threadId,
  );
}

type ChatTabMention = NonNullable<ChatTabType['mentions']>[number];

const serializeChatTabMentions = (
  mentions: ChatTabType['mentions'],
): ChatTabType['mentions'] =>
  mentions?.map(
    (mention): ChatTabMention => ({
      id: mention.id,
      label: mention.label,
      type: mention.type,
      mentionEntity: mention.mentionEntity,
    }),
  );

type LegacyChatTab = ChatTabType & { applicationId?: string };

function normalizeChatTab(tab: LegacyChatTab): ChatTabType {
  return {
    tabId: tab.tabId,
    chatId: tab.chatId,
    conversationId: tab.conversationId,
    title: tab.title,
    hasCustomTitle: tab.hasCustomTitle,
    targetId: tab.targetId ?? tab.applicationId,
    targetType:
      tab.targetType ??
      (tab.applicationId ? TARGET_TYPE.WEB_APPLICATION : undefined),
    mentions: tab.mentions,
    draftInput: tab.draftInput,
    skillDraftId: tab.skillDraftId,
    linkedObject: tab.linkedObject,
  };
}

export function tabBelongsToTarget({
  tab,
  targetId,
  targetType,
}: {
  tab: ChatTabType;
  targetId: string;
  targetType: TargetTypeType;
}): boolean {
  if (!tab.targetId || !tab.targetType) {
    return false;
  }

  return tab.targetId === targetId && tab.targetType === targetType;
}

export function resolveScopedActiveChatTabId({
  chatTabs,
  activeChatTabId,
  targetId,
  targetType,
}: {
  chatTabs: ChatTabType[];
  activeChatTabId: string | null;
  targetId: string;
  targetType: TargetTypeType;
}): string | null {
  const scopedTabs = chatTabs.filter((tab) =>
    tabBelongsToTarget({ tab, targetId, targetType }),
  );

  if (scopedTabs.some((tab) => tab.tabId === activeChatTabId)) {
    return activeChatTabId;
  }

  return scopedTabs.at(-1)?.tabId ?? null;
}

function tabHasNoTargetScope(tab: ChatTabType): boolean {
  return !tab.targetId;
}

function chatInstanceHasUserMessages(chatId: string): boolean {
  const chat = useChatInstancesStore.getState().chats.get(chatId);

  return chat?.messages.some((message) => message.role === 'user') ?? false;
}

function tabHasPreservedDraftContent(tab: ChatTabType): boolean {
  if (tab.conversationId) {
    return false;
  }

  if (tab.draftInput?.trim()) {
    return true;
  }

  if (tab.mentions?.length) {
    return true;
  }

  if (tab.skillDraftId || tab.linkedObject) {
    return true;
  }

  if (chatInstanceHasUserMessages(tab.chatId)) {
    return true;
  }

  return (
    useChatInstancesStore.getState().getQueuedMessages({ chatId: tab.chatId })
      .length > 0
  );
}

export interface ChatTabsState {
  chatTabs: ChatTabType[];
  activeChatTabId: string | null;

  // Actions
  setChatTabs: (tabs: ChatTabType[]) => void;
  setActiveChatTabId: (tabId: string | null) => void;
  addChatTab: (tab: ChatTabType) => void;
  removeChatTab: (tabId: string) => void;
  updateChatTab: (tabId: string, updates: Partial<ChatTabType>) => void;
  clearChatTabs: () => void;
  closeTab: ({ tabId }: { tabId: string }) => void;
  closeAll: ({ exceptTabId }: { exceptTabId?: string }) => void;
}

const MAX_CHAT_TABS = 10;

const syncActiveTabUnreadState = (activeChatTabId: string | null) => {
  if (activeChatTabId) {
    useUnreadTabsStore.getState().markTabRead(activeChatTabId);
  }
};

export const useChatTabsStore = create<ChatTabsState>()(
  persist(
    (set, get) => ({
      chatTabs: [],
      activeChatTabId: null,

      setChatTabs: (tabs) => {
        set({ chatTabs: tabs });
      },

      setActiveChatTabId: (tabId) => {
        set({ activeChatTabId: tabId });
        syncActiveTabUnreadState(tabId);
      },

      addChatTab: (tab) => {
        const { chatTabs, activeChatTabId } = get();

        const updatedTabs = [...chatTabs];
        const serializableTab: ChatTabType = {
          ...tab,
          mentions: serializeChatTabMentions(tab.mentions),
        };

        if (updatedTabs.length >= MAX_CHAT_TABS) {
          const evictableTabIndex = updatedTabs.findIndex(
            (tab) =>
              tab.tabId !== activeChatTabId &&
              !tabHasPreservedDraftContent(tab),
          );

          if (evictableTabIndex < 0) {
            return;
          }

          const evictedTab = updatedTabs[evictableTabIndex];

          useUnreadTabsStore.getState().markTabRead(evictedTab.tabId);
          useChatInstancesStore.getState().removeConfig(evictedTab.chatId);
          updatedTabs.splice(evictableTabIndex, 1);
        }

        updatedTabs.push(serializableTab);

        set({ chatTabs: updatedTabs, activeChatTabId: serializableTab.tabId });
        syncActiveTabUnreadState(serializableTab.tabId);
      },

      removeChatTab: (tabId) => {
        const { chatTabs, activeChatTabId } = get();
        const tabToRemove = chatTabs.find((tab) => tab.tabId === tabId);

        if (!tabToRemove) return;

        const remainingTabs = chatTabs.filter((tab) => tab.tabId !== tabId);
        const updates: Partial<ChatTabsState> = { chatTabs: remainingTabs };

        if (tabId === activeChatTabId && remainingTabs.length > 0) {
          updates.activeChatTabId =
            remainingTabs[remainingTabs.length - 1].tabId;
        } else if (remainingTabs.length === 0) {
          updates.activeChatTabId = null;
        }

        set(updates);
      },

      updateChatTab: (tabId, updates) => {
        const { chatTabs } = get();
        const existingTab = chatTabs.find((tab) => tab.tabId === tabId);

        if (!existingTab) {
          return;
        }

        const nextUpdates: Partial<ChatTabType> =
          updates.mentions === undefined
            ? updates
            : {
                ...updates,
                mentions: serializeChatTabMentions(updates.mentions),
              };
        const nextTab = { ...existingTab, ...nextUpdates };

        if (isEqual(existingTab, nextTab)) {
          return;
        }

        set({
          chatTabs: chatTabs.map((tab) =>
            tab.tabId === tabId ? nextTab : tab,
          ),
        });
      },

      clearChatTabs: () => {
        const { chatTabs } = get();

        const chatInstancesStore = useChatInstancesStore.getState();

        chatTabs.forEach((tab) => {
          chatInstancesStore.removeConfig(tab.chatId);
        });

        useUnreadTabsStore.getState().clearAll();
        set({ chatTabs: [], activeChatTabId: null });
      },

      closeTab: ({ tabId }) => {
        const { chatTabs, activeChatTabId } = get();

        const tabToRemove = chatTabs.find((tab) => tab.tabId === tabId);

        if (!tabToRemove) return;

        useUnreadTabsStore.getState().markTabRead(tabId);

        // Clean up chat instance when tab is closed
        const chatInstancesStore = useChatInstancesStore.getState();

        chatInstancesStore.removeConfig(tabToRemove.chatId);

        const remainingTabs = chatTabs.filter((tab) => tab.tabId !== tabId);

        if (tabId === activeChatTabId) {
          const nextActiveTabId =
            remainingTabs.length > 0
              ? remainingTabs[remainingTabs.length - 1].tabId
              : null;

          set({
            chatTabs: remainingTabs,
            activeChatTabId: nextActiveTabId,
          });
        } else {
          set({
            chatTabs: remainingTabs,
          });
        }
      },
      closeAll: ({ exceptTabId } = {}) => {
        const { chatTabs } = get();
        const exceptTab = chatTabs.find((tab) => tab.tabId === exceptTabId);

        chatTabs.forEach((tab) => {
          if (tab.tabId !== exceptTabId) {
            useUnreadTabsStore.getState().markTabRead(tab.tabId);
          }
        });

        // Clean up chat instances for all closed tabs
        const chatInstancesStore = useChatInstancesStore.getState();

        chatTabs.forEach((tab) => {
          if (tab.tabId !== exceptTabId) {
            chatInstancesStore.removeConfig(tab.chatId);
          }
        });

        const newActiveTabId = exceptTab?.tabId ?? null;
        const newTabs = exceptTab ? [exceptTab] : [];

        set({
          chatTabs: newTabs,
          activeChatTabId: newActiveTabId,
        });
      },
    }),
    {
      name: 'terra-proxy-chat-tabs-state',
      storage: createJSONStorage(() => createTenantStorage()),
      partialize: (state) => {
        const currentTargetId = useProjectStore.getState().targetId;
        const currentTargetType = useProjectStore.getState().targetType;

        const chatTabsForCurrentTarget = currentTargetId
          ? state.chatTabs.filter(
              (tab) =>
                tabHasNoTargetScope(tab) ||
                tabBelongsToTarget({
                  tab,
                  targetId: currentTargetId,
                  targetType: currentTargetType,
                }),
            )
          : state.chatTabs.filter((tab) => tabHasNoTargetScope(tab));

        return {
          chatTabs: chatTabsForCurrentTarget.map((tab) => ({
            ...normalizeChatTab(tab),
            mentions: toPersistedChatTabMentions(tab.mentions),
          })),
          activeChatTabId: chatTabsForCurrentTarget.some(
            (tab) => tab.tabId === state.activeChatTabId,
          )
            ? state.activeChatTabId
            : chatTabsForCurrentTarget.length > 0
              ? chatTabsForCurrentTarget[chatTabsForCurrentTarget.length - 1]
                  .tabId
              : null,
        };
      },
      onRehydrateStorage: () => (state) => {
        if (!state) {
          return;
        }

        state.chatTabs = (state.chatTabs ?? []).map((tab) =>
          normalizeChatTab(tab as LegacyChatTab),
        );
      },
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<ChatTabsState> | undefined;

        return {
          ...currentState,
          ...persisted,
          chatTabs: persisted?.chatTabs ?? currentState.chatTabs ?? [],
        };
      },
    },
  ),
);

export const useChatTabs = () => {
  const currentTargetId = useTargetId();
  const currentTargetType = useTargetType();
  const allChatTabs = useChatTabsStore((state) => state.chatTabs ?? []);

  const prevFilteredRef = useRef<ChatTabType[]>([]);
  const prevTargetScopeRef = useRef<string | undefined>(
    currentTargetId ? `${currentTargetType}:${currentTargetId}` : undefined,
  );
  const prevAllTabsRef = useRef<ChatTabType[]>([]);

  const chatTabs = useMemo(() => {
    const currentScopeKey = currentTargetId
      ? `${currentTargetType}:${currentTargetId}`
      : undefined;
    const targetScopeChanged = prevTargetScopeRef.current !== currentScopeKey;

    prevTargetScopeRef.current = currentScopeKey;

    const filterTabsForCurrentTarget = (tabs: ChatTabType[]) =>
      currentTargetId
        ? tabs.filter((tab) =>
            tabBelongsToTarget({
              tab,
              targetId: currentTargetId,
              targetType: currentTargetType,
            }),
          )
        : tabs.filter((tab) => tabHasNoTargetScope(tab));

    const currentAppTabIds = new Set(
      filterTabsForCurrentTarget(allChatTabs).map((tab) => tab.tabId),
    );
    const prevAppTabIds = new Set(
      filterTabsForCurrentTarget(prevAllTabsRef.current).map(
        (tab) => tab.tabId,
      ),
    );

    const tabsChanged =
      targetScopeChanged ||
      currentAppTabIds.size !== prevAppTabIds.size ||
      Array.from(currentAppTabIds).some((id) => !prevAppTabIds.has(id)) ||
      Array.from(prevAppTabIds).some((id) => !currentAppTabIds.has(id));

    let contentChanged = false;

    if (!tabsChanged && !targetScopeChanged) {
      const currentFiltered = filterTabsForCurrentTarget(allChatTabs);

      for (const tab of currentFiltered) {
        const prevTab = prevFilteredRef.current.find(
          (filteredTab) => filteredTab.tabId === tab.tabId,
        );

        if (
          !prevTab ||
          tab.title !== prevTab.title ||
          tab.hasCustomTitle !== prevTab.hasCustomTitle ||
          tab.conversationId !== prevTab.conversationId ||
          tab.chatId !== prevTab.chatId ||
          tab.skillDraftId !== prevTab.skillDraftId ||
          tab.draftInput !== prevTab.draftInput ||
          !isEqual(tab.mentions, prevTab.mentions) ||
          !isEqual(tab.linkedObject, prevTab.linkedObject)
        ) {
          contentChanged = true;
          break;
        }
      }
    }

    if (!tabsChanged && !contentChanged && prevFilteredRef.current.length > 0) {
      prevAllTabsRef.current = allChatTabs;

      return prevFilteredRef.current;
    }

    const filtered = filterTabsForCurrentTarget(allChatTabs);

    prevFilteredRef.current = filtered;
    prevAllTabsRef.current = allChatTabs;

    return filtered;
  }, [allChatTabs, currentTargetId, currentTargetType]);

  const activeChatTabId = useChatTabsStore((state) => state.activeChatTabId);

  const {
    setChatTabs,
    setActiveChatTabId,
    addChatTab,
    removeChatTab,
    updateChatTab,
    clearChatTabs,
    closeTab,
    closeAll,
  } = useChatTabsStore(
    useShallow((state) => ({
      setChatTabs: state.setChatTabs,
      setActiveChatTabId: state.setActiveChatTabId,
      addChatTab: state.addChatTab,
      removeChatTab: state.removeChatTab,
      updateChatTab: state.updateChatTab,
      clearChatTabs: state.clearChatTabs,
      closeTab: state.closeTab,
      closeAll: state.closeAll,
    })),
  );

  const activeChatTab = useMemo(
    () => chatTabs.find((tab) => tab.tabId === activeChatTabId),
    [chatTabs, activeChatTabId],
  );

  return {
    chatTabs,
    activeChatTabId,
    activeChatTab,
    setChatTabs,
    setActiveChatTabId,
    addChatTab,
    removeChatTab,
    updateChatTab,
    clearChatTabs,
    closeTab,
    closeAll,
  };
};
